// Input: single-line text input atom (search boxes, inline forms). The wrapper
// owns the border and focus ring; the native input is borderless inside it.

import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'
import clsx from 'clsx'
import css from './Input.module.css'

export type InputProps = {
  /** Optional 16px leading icon node. */
  icon?: ReactNode
  /** Paints the error border (sets aria-invalid when not supplied). */
  invalid?: boolean
  /** Class for the wrapper span, so callers can size the control. */
  className?: string
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'className'>

/**
 * Render a text input with an optional leading icon.
 * @param props - icon, invalid flag, native input attributes.
 * @param ref - the native input.
 * @returns wrapper span containing the native input.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { icon, invalid = false, className, ...rest },
  ref,
) {
  return (
    <span className={clsx(css.wrap, invalid && css.invalid, className)}>
      {icon != null && <span className={css.icon}>{icon}</span>}
      <input
        ref={ref}
        className={css.input}
        aria-invalid={invalid || rest['aria-invalid'] || undefined}
        {...rest}
      />
    </span>
  )
})
